<template>
    <ds-modal
        class="sms-summary"
        :is-open="isOpen"
        show-close
        sticky-desktop-header
        size="fullscreen"
        intercept-close
        @close="close"
    >
        <div class="desktop-header">
            <h4 class="broadcast-title" :title="broadcast.title">
                {{ broadcast.title }}
            </h4>

            <SmsBroadcastStatusIndicator
                v-if="!loading"
                class="status-indicator"
                :status="broadcast.status"
            />

            <div
                class="actions"
            >
                <ds-text-button
                    v-if="showButton"
                    :class="resumeClass"
                    dense
                    @click="resumeSending"
                >
                    {{ resumeText }}
                </ds-text-button>
                <ds-filled-button
                    class="copy-action"
                    dense
                    @click="action"
                >
                    {{ actionButtonText }}
                </ds-filled-button>
            </div>
        </div>

        <div class="desktop-body">
            <section class="sms-broadcast-summary-info">
                <h4 class="section-title">
                    {{ $options.MESSAGE_CONSTANTS.SUMMARY }}
                </h4>

                <div class="summary-row">
                    <small class="label">
                        {{ recipientCountText }}
                    </small>

                    <div class="recipient-chips">
                        <ds-chip
                            v-for="{ id, name, icon } in recipientData.criteria"
                            :key="id"
                            class="recipient-chip"
                        >
                            <template #default>
                                <span>{{ name }}</span>
                            </template>

                            <template #leading>
                                <ds-icon :name="icon" />
                            </template>
                        </ds-chip>
                    </div>
                </div>

                <div class="summary-row">
                    <small class="label">
                        {{ $options.MESSAGE_CONSTANTS.MESSAGE }}
                    </small>

                    <span class="field">
                        {{ broadcast.messageContent }}
                    </span>
                </div>

                <div class="summary-row data-row">
                    <small class="label">
                        {{ scheduledBy }}
                    </small>
                    <span class="field">
                        {{ scheduledDate }}
                    </span>
                </div>

                <div
                    v-if="broadcast.status === $options.SMS_BROADCAST_STATUS.STOPPED"
                    class="summary-row data-row"
                >
                    <small class="label">
                        {{ stoppedLabel }}
                    </small>

                    <span class="field">
                        {{ stoppedDate }}
                    </span>
                </div>

                <div class="content-wrapper">
                    <SmsBroadcastStatistics
                        v-if="broadcast.smsBroadcastStatistics"
                        :statistics="broadcast.smsBroadcastStatistics"
                        :status="broadcast.status"
                    />
                </div>
            </section>

            <section class="sms-broadcast-summary-preview">
                <div class="centered">
                    <span class="section-title">
                        {{ $options.MESSAGE_CONSTANTS.PREVIEW }}
                    </span>

                    <SmsBroadcastPreview
                        :message="broadcast.messageContent"
                    />
                </div>
            </section>
        </div>
    </ds-modal>
</template>

<script>
import SmsBroadcastStatusIndicator from '@/components/broadcast/sms/SmsBroadcastStatusIndicator';
import SmsBroadcastPreview from '@/components/broadcast/sms/SmsBroadcastPreview';
import SmsBroadcastStatistics from '@/components/broadcast/sms/SmsBroadcastStatistics';
import {
    SMS_BROADCAST_STATUS,
    SMS_BROADCAST_STATUS_MESSAGE,
} from '../constants/smsBroadcast.constants';
import { longDateFormat } from '../utils/date.util';
import { getUserList } from '@/api/broadcasts/smsBroadcasts';
import { showEditForStoppedBroadcast } from '@/components/broadcast/utils/smsBroadcast.util';


export const MESSAGE_CONSTANTS = {
    SAVE: 'Save',
    TITLE: 'Marketing Number Settings',
    SUMMARY: 'Summary',
    PERFORMANCE: 'Performance',
    MESSAGE: 'Message',
    PREVIEW: 'Preview',
    COPY: 'Make a copy',
    STOP: 'Stop sending',
    RESUME: 'Resume sending',
    EDIT: 'Edit and send',
    SENDING: 'Sending to',
    SCHEDULED: 'Scheduled to send by',
    ON: 'on',
    SUCCESS_MESSAGE: 'Broadcast resumed successfully',
    SENT_TO_X_CONTACTS: 'Sent to {total} contacts',
    SENT_TO_X_OF_Y_CONTACTS: 'Sent to {actual} of {total} contacts',
    SENT_BY: 'Sent by {fullName} on',
    NO_ACCESS: 'Unable to get user info',
    STOPPED_STATUS: 'Stopped on',
    STOPPED_BY: 'Stopped by {fullName} on',
};

export default {

    getUserList,
    longDateFormat,
    MESSAGE_CONSTANTS,
    SMS_BROADCAST_STATUS,

    components: {
        SmsBroadcastStatusIndicator,
        SmsBroadcastPreview,
        SmsBroadcastStatistics,
    },

    props: {
        broadcast: {
            type: Object,
        },
        isOpen: {
            type: Boolean,
            default: false,
        },
    },

    data () {
        return {
            loading: false,
            isSpaApp: false,
            recipientData: {
                criteria: JSON.parse(this.broadcast.recipientCriteria),
            },
            users: [],
            username: '',
            stoppedBy: '',
        };
    },

    mounted () {
        this.loadUsers();
    },

    watch: {
        users() {
            let user = this.users.find((element) => element.id === parseInt(this.broadcast.updatedBy, 10));

            if (user) {
                this.username = user.username;
            }

            if (this.broadcast.stoppedBy) {
                user = null;
                user = this.users.find((element) => element.id === parseInt(this.broadcast.stoppedBy, 10));

                if (user) {
                    this.stoppedBy = user.username;
                }
            }
        },
    },

    computed: {
        recipientCountText() {
            let response = MESSAGE_CONSTANTS.SENDING;

            if (this.broadcast.smsBroadcastStatistics) {
                const sent = `${this.broadcast.smsBroadcastStatistics.sent}`;
                const total = `${this.broadcast.smsBroadcastStatistics.total}`;

                response = this.broadcast.smsBroadcastStatistics.total === this.broadcast.smsBroadcastStatistics.sent
                    ? MESSAGE_CONSTANTS.SENT_TO_X_CONTACTS.replace('{total}', sent)
                    : MESSAGE_CONSTANTS.SENT_TO_X_OF_Y_CONTACTS.replace('{actual}', sent).replace('{total}', total);
            }

            return response;
        },

        scheduledBy() {
            return (this.broadcast.status === SMS_BROADCAST_STATUS.STOPPED
                || this.broadcast.status === SMS_BROADCAST_STATUS.SCHEDULED)
                ? `${SMS_BROADCAST_STATUS_MESSAGE.SCHEDULED} to send by ${this.username} ${MESSAGE_CONSTANTS.ON}`
                : `${SMS_BROADCAST_STATUS_MESSAGE[this.broadcast.status]} by ${this.username} ${MESSAGE_CONSTANTS.ON}`;
        },

        scheduledDate() {
            const {
                scheduleDate,
            } = this.broadcast;

            return longDateFormat(scheduleDate);
        },

        stoppedLabel() {
            if (!this.broadcast.stoppedBy || !this.stoppedBy) {
                return MESSAGE_CONSTANTS.STOPPED_STATUS;
            }

            return MESSAGE_CONSTANTS.STOPPED_BY.replace('{fullName}', this.stoppedBy);
        },

        stoppedDate() {
            const {
                updated,
            } = this.broadcast;

            return longDateFormat(updated);
        },

        actionButtonText() {
            return this.broadcast.status === SMS_BROADCAST_STATUS.SCHEDULED
                ? MESSAGE_CONSTANTS.STOP
                : MESSAGE_CONSTANTS.COPY;
        },

        resumeClass() {
            return this.broadcast.status === SMS_BROADCAST_STATUS.STOPPED
                ? 'resume-action'
                : 'resume-action hide-element';
        },

        resumeText() {
            return showEditForStoppedBroadcast(this.broadcast.scheduleDate)
                ? this.$options.MESSAGE_CONSTANTS.EDIT
                : this.$options.MESSAGE_CONSTANTS.RESUME;
        },

        showButton() {
            const { STOPPED } = SMS_BROADCAST_STATUS;

            return this.broadcast.status === STOPPED;
        },
    },

    methods: {
        async loadUsers () {
            const vm = this;

            getUserList()
                .then((response) => {
                    vm.users = response.data;
                })
                .catch(() => {
                    this.$error({ message: MESSAGE_CONSTANTS.NO_ACCESS });
                });
        },

        close() {
            this.$emit('close');
        },

        action() {
            if (this.broadcast.status === SMS_BROADCAST_STATUS.SCHEDULED) {
                this.$emit('stop', this.broadcast.id);
                this.$emit('close');
            } else {
                this.$emit('copy', this.broadcast.id);
            }
        },
        resumeSending() {
            this.$emit('resume', this.broadcast.id);
        },
    },
};
</script>

<style lang="scss">
    div.sms-summary div.desktop-header {
        position: fixed;
        top: .75rem;
        left: 4rem;
        align-items: center;
        display: inline-grid;
        grid-template-columns: max-content auto 260px;
        width: 95%;
        z-index: 10;
    }

    div.sms-summary div.desktop-header > div.status-indicator {
        margin-left: 1rem;
    }

    div.sms-summary div.modal-body {
        height: 100%;
        padding: 0;
    }

    div.sms-summary div.modal-body-inner {
        padding: 0 !important;
    }

    div.sms-summary div.desktop-body {
        display: inline-grid;
        grid-template-columns: 62% 38%;
        height: 100%;
    }

    div.sms-summary div.desktop-body  h4.section-title {
        font-family: Ivar Soft,Times New Roman;
        font-weight: 700;
        margin-bottom: 1.6rem;
        font-size: 1.25rem;
    }

    div.sms-summary div.desktop-body  span.section-title {
        color: $color-ink-800;
    }

    div.sms-summary div.desktop-body > section.sms-broadcast-summary-info {
        background-color: white;
        padding: 2.1rem 0 0 5rem;
    }

    div.sms-summary div.desktop-body > section.sms-broadcast-summary-preview {
        background-color: $color-gray-050;
        border-left: 1px solid rgba(0, 0, 0, 0.09);
        padding: 2.5rem 0 0 0;
        display: inline-grid;
        grid-template-columns: max-content;
        justify-content: center;
    }

    div.sms-summary div.modal-overlay {
        overflow: hidden !important;
    }

    div.sms-summary small.label {
        padding-right: .5rem;
        font-size: .75rem;
        margin-bottom: .5rem;
        color: $color-gray-800;
    }

    div.sms-summary div.recipient-chip {
        margin-right: .5rem;
        margin-bottom: .5rem;
    }

    div.sms-summary div.summary-row {
        display: inline-grid;
        padding-bottom: 1rem;
        width: 100%;
        font-size: 1rem;
    }

    div.sms-summary div.data-row {
        padding-top: .5rem;
    }

    div.sms-summary div.actions {
        display: inline-grid;
        grid-template-columns: auto auto;
    }

    div.sms-summary div.actions > button.copy-action,
    div.sms-summary div.actions > button.resume-action{
        width: fit-content;
        height: fit-content;
    }

    div.sms-summary hide-element {
        display: block;
        visibility: hidden;
    }
</style>
